import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  TextField, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Tabs,
  Tab,
  FormHelperText
} from '@mui/material';
import { 
  Save as SaveIcon, 
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Email as EmailIcon,
  Telegram as TelegramIcon
} from '@mui/icons-material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import RichTextEditor from '../../components/common/RichTextEditor';

const AdvertisementForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const fileInputRef = useRef(null);
  const additionalImageInputRef = useRef(null);
  
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [advertisement, setAdvertisement] = useState({
    title: '',
    content: '',
    status: 'draft',
    emailSubject: '',
    telegramPreview: '',
    featuredImage: null
  });
  const [additionalImages, setAdditionalImages] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [previewMode, setPreviewMode] = useState('email');
  const [validation, setValidation] = useState({
    title: true,
    content: true,
    emailSubject: true,
    telegramPreview: true
  });
  const [imageUploading, setImageUploading] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      fetchAdvertisement();
    }
  }, [id]);

  const fetchAdvertisement = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`/api/advertisements/${id}`);
      const data = response.data || {};

      // Merge backend data into current state and map telegramSubject -> telegramPreview
      setAdvertisement(prev => ({
        ...prev,
        ...data,
        telegramPreview: data.telegramPreview ?? data.telegramSubject ?? prev.telegramPreview ?? ''
      }));
      
      // Fetch additional images
      const imagesResponse = await axios.get(`/api/advertisements/${id}/images`);
      setAdditionalImages(imagesResponse.data);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching advertisement:', err);
      setError('Ошибка при загрузке рекламного материала');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAdvertisement(prev => ({ ...prev, [name]: value }));
    
    // Clear validation error when field is edited
    if (validation[name] === false) {
      setValidation(prev => ({ ...prev, [name]: true }));
    }
  };

  const handleContentChange = (content) => {
    setAdvertisement(prev => ({ ...prev, content }));
    
    // Clear validation error when content is edited
    if (validation.content === false) {
      setValidation(prev => ({ ...prev, content: true }));
    }
  };

  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      setImageUploading(true);
      const formData = new FormData();
      formData.append('featuredImage', file);
      
      if (isEditMode) {
        const response = await axios.post(`/api/advertisements/${id}/featured-image`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        setAdvertisement(prev => ({ ...prev, featuredImage: response.data.featuredImage }));
      } else {
        const reader = new FileReader();
        reader.onload = (event) => {
          setAdvertisement(prev => ({ ...prev, featuredImage: event.target.result }));
        };
        reader.readAsDataURL(file);
      }
    } catch (err) {
      console.error('Error uploading featured image:', err);
      setError('Ошибка при загрузке изображения');
    } finally {
      setImageUploading(false);
    }
  };

  const handleAdditionalImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !isEditMode) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`/api/advertisements/${id}/images`, formData);
      setAdditionalImages(prev => [...prev, response.data]);
    } catch (err) {
      console.error('Error uploading additional image:', err);
      setError('Ошибка при загрузке дополнительного изображения');
    }
  };

  const handleDeleteAdditionalImage = async (imageId) => {
    if (!isEditMode) return;
    
    try {
      await axios.delete(`/api/advertisements/images/${imageId}`);
      setAdditionalImages(prev => prev.filter(img => img.id !== imageId));
    } catch (err) {
      console.error('Error deleting additional image:', err);
      setError('Ошибка при удалении дополнительного изображения');
    }
  };

  const validateForm = () => {
    const newValidation = {
      title: !!(advertisement.title || '').trim(),
      content: !!(advertisement.content || '').trim(),
      emailSubject: !!(advertisement.emailSubject || '').trim(),
      telegramPreview: !!(advertisement.telegramPreview || '').trim()
    };
    
    setValidation(newValidation);
    return Object.values(newValidation).every(v => v === true);
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    try {
      setSaving(true);
      setError(null);
      
      // Build minimal payload without featuredImage base64 to avoid oversized JSON bodies
      const payload = {
        title: advertisement.title,
        content: advertisement.content,
        emailSubject: advertisement.emailSubject,
        status: advertisement.status,
        // Map Telegram preview to backend field
        telegramSubject: advertisement.telegramPreview || advertisement.telegramSubject || ''
      };

      let response;
      if (isEditMode) {
        response = await axios.put(`/api/advertisements/${id}`, payload);
      } else {
        response = await axios.post('/api/advertisements', payload);
      }
      
      setSaving(false);
      setSuccessMessage(`Рекламный материал успешно ${isEditMode ? 'обновлен' : 'создан'}`);
      
      if (!isEditMode) {
        // Redirect to edit page after creation
        navigate(`/admin/advertisements/edit/${response.data.id}`);
      }
    } catch (err) {
      console.error('Error saving advertisement:', err);
      setError(`Ошибка при ${isEditMode ? 'обновлении' : 'создании'} рекламного материала`);
      setSaving(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handlePreviewModeChange = (mode) => {
    setPreviewMode(mode);
  };

  const renderEmailPreview = () => {
    return (
      <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, bgcolor: '#f5f5f5' }}>
        <Typography variant="subtitle2" gutterBottom sx={{ mb: 2, color: '#333' }}>
          Предпросмотр Email
        </Typography>
        
        {/* Email style container */}
        <Box sx={{ 
          bgcolor: '#ffffff', 
          borderRadius: 2, 
          p: 3, 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          maxWidth: 600,
          mx: 'auto',
          border: '1px solid #e0e0e0'
        }}>
          <Box sx={{ 
            bgcolor: '#f8f9fa',
            borderRadius: 1,
            p: 2,
            mb: 3,
            border: '1px solid #eaeaea'
          }}>
            <Typography variant="subtitle1" sx={{ color: '#555', fontWeight: 500 }}>
              <strong>Тема:</strong> {advertisement.emailSubject || '(не указана)'}
            </Typography>
          </Box>
          
          {advertisement.featuredImage && (
            <Box sx={{ mb: 3, textAlign: 'center', borderRadius: 1, overflow: 'hidden' }}>
              <img 
                src={advertisement.featuredImage} 
                alt={advertisement.title}
                style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
              />
            </Box>
          )}
          
          <Typography variant="h5" gutterBottom sx={{ color: '#333', fontWeight: 'bold', borderBottom: '1px solid #eee', pb: 1 }}>
            {advertisement.title || '(без заголовка)'}
          </Typography>
          
          <Box sx={{ mt: 2, color: '#333', bgcolor: '#ffffff' }}>
            <div 
              dangerouslySetInnerHTML={{ __html: advertisement.content || '<p>(без содержания)</p>' }} 
              style={{ lineHeight: 1.6 }}
            />
          </Box>
          
          <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #eee', textAlign: 'center' }}>
            <Button 
              variant="contained" 
              size="small" 
              sx={{ 
                bgcolor: '#4caf50', 
                '&:hover': { bgcolor: '#388e3c' } 
              }}
            >
              Подробнее на сайте
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderTelegramPreview = () => {
    return (
      <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, bgcolor: '#f5f5f5' }}>
        <Typography variant="subtitle2" gutterBottom sx={{ mb: 2, color: '#333' }}>
          Предпросмотр в Telegram
        </Typography>
        
        {/* Telegram message style */}
        <Box sx={{ 
          bgcolor: '#1e94d3', // Telegram blue background
          borderRadius: 2, 
          p: 2, 
          boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
          maxWidth: 400,
          mx: 'auto',
          color: '#fff' // White text for contrast
        }}>
          <Box sx={{ 
            bgcolor: '#ffffff',
            borderRadius: 1,
            p: 2,
            mb: 2,
            color: '#333' // Dark text on white background
          }}>
            {advertisement.featuredImage && (
              <Box sx={{ mb: 2, borderRadius: 1, overflow: 'hidden' }}>
                <img 
                  src={advertisement.featuredImage} 
                  alt={advertisement.title}
                  style={{ width: '100%', maxHeight: '200px', objectFit: 'cover' }}
                />
              </Box>
            )}
            
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>
              {advertisement.title || '(без заголовка)'}
            </Typography>
            
            <Typography variant="body2" sx={{ color: '#555' }} gutterBottom>
              {advertisement.telegramPreview || '(без предпросмотра)'}
            </Typography>
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                size="small" 
                sx={{ 
                  bgcolor: '#1e94d3', 
                  '&:hover': { bgcolor: '#0d84c3' } 
                }}
              >
                Подробнее
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {isEditMode ? 'Редактирование рекламы' : 'Создание рекламы'}
        </Typography>
        
        <Button
          component={Link}
          to="/admin/advertisements"
          startIcon={<ArrowBackIcon />}
          variant="outlined"
        >
          Назад к списку
        </Button>
      </Box>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="Основная информация" />
          {isEditMode && <Tab label="Дополнительные изображения" />}
        </Tabs>
        
        <Grid container spacing={3} sx={{ display: tabValue === 0 ? 'flex' : 'none' }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Заголовок"
              name="title"
              value={advertisement.title}
              onChange={handleInputChange}
              fullWidth
              required
              error={!validation.title}
              helperText={!validation.title && 'Заголовок обязателен'}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Статус</InputLabel>
              <Select
                name="status"
                value={advertisement.status}
                onChange={handleInputChange}
                label="Статус"
              >
                <MenuItem value="draft">Черновик</MenuItem>
                <MenuItem value="ready">Готов к отправке</MenuItem>
                <MenuItem value="sent">Отправлен</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="Тема email"
              name="emailSubject"
              value={advertisement.emailSubject}
              onChange={handleInputChange}
              fullWidth
              required
              error={!validation.emailSubject}
              helperText={!validation.emailSubject && 'Тема email обязательна'}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="Предпросмотр для Telegram"
              name="telegramPreview"
              value={advertisement.telegramPreview || ''}
              onChange={handleInputChange}
              fullWidth
              required
              error={!validation.telegramPreview}
              helperText={!validation.telegramPreview && 'Предпросмотр для Telegram обязателен'}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Главное изображение
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {advertisement.featuredImage ? (
                <Box sx={{ position: 'relative', width: 150, height: 150 }}>
                  <img 
                    src={advertisement.featuredImage} 
                    alt="Featured"
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      borderRadius: '4px'
                    }}
                  />
                </Box>
              ) : (
                <Box 
                  sx={{ 
                    width: 150, 
                    height: 150, 
                    border: '2px dashed #ccc', 
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column'
                  }}
                >
                  <ImageIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Нет изображения
                  </Typography>
                </Box>
              )}
              
              <Box>
                <Button 
                  variant="outlined"
                  onClick={() => fileInputRef.current.click()}
                  disabled={imageUploading}
                >
                  {advertisement.featuredImage ? 'Изменить' : 'Загрузить'} изображение
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept="image/*"
                  onChange={handleFeaturedImageUpload}
                />
                {imageUploading && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      Загрузка изображения...
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Содержание рекламы (HTML)
            </Typography>
            
            <RichTextEditor 
              value={advertisement.content} 
              onChange={handleContentChange}
              error={!validation.content}
            />
            
            {!validation.content && (
              <FormHelperText error>
                Содержание рекламы обязательно
              </FormHelperText>
            )}
          </Grid>
        </Grid>
        
        <Grid container spacing={2} sx={{ display: tabValue === 1 ? 'flex' : 'none' }}>
          {isEditMode && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Дополнительные изображения
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {additionalImages.map(image => (
                    <Grid item xs={6} sm={4} md={3} key={image.id}>
                      <Card>
                        <CardMedia
                          component="img"
                          height="140"
                          image={image.imageUrl}
                          alt={image.caption || 'Additional image'}
                        />
                        <CardContent sx={{ p: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" noWrap>
                              {image.caption || 'Без подписи'}
                            </Typography>
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteAdditionalImage(image.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                  
                  <Grid item xs={6} sm={4} md={3}>
                    <Box 
                      sx={{ 
                        border: '2px dashed #ccc', 
                        borderRadius: 1, 
                        p: 1, 
                        textAlign: 'center',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                      onClick={() => additionalImageInputRef.current.click()}
                    >
                      <ImageIcon sx={{ fontSize: 24, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2">Добавить</Typography>
                    </Box>
                    
                    <input
                      type="file"
                      ref={additionalImageInputRef}
                      style={{ display: 'none' }}
                      accept="image/*"
                      onChange={handleAdditionalImageUpload}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </Box>
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Предпросмотр
        </Typography>
        
        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
          <Button
            variant={previewMode === 'email' ? 'contained' : 'outlined'}
            startIcon={<EmailIcon />}
            onClick={() => handlePreviewModeChange('email')}
            size="small"
          >
            Email
          </Button>
          <Button
            variant={previewMode === 'telegram' ? 'contained' : 'outlined'}
            startIcon={<TelegramIcon />}
            onClick={() => handlePreviewModeChange('telegram')}
            size="small"
          >
            Telegram
          </Button>
        </Box>
        
        {previewMode === 'email' ? renderEmailPreview() : renderTelegramPreview()}
      </Paper>
      
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdvertisementForm;
